// frontend/src/selectors/paymentSelectors.js

const EMPTY_BRANCH = Object.freeze({
  loading: false,
  success: false,
  error: null,
  errorCode: "",
  status: 0,
  requestId: "",
  retryAfterSeconds: 0,
  message: "",
});

const EMPTY_CURRENT = Object.freeze({
  order: null,
  orderNumber: "",
  payment: null,
  clientSecret: "",
  customerCheckout: null,
  paymentWindowExpiresAt: "",
  approvalUrl: "",
  approveUrl: "",
  payerActionUrl: "",
  checkoutUrl: "",
  nextAction: null,
});

const EMPTY_PAYMENT_STATE = Object.freeze({});

const EMPTY_PAYMENT_CONFIG = Object.freeze({
  ...EMPTY_BRANCH,
  loaded: false,
  enabledProviders: Object.freeze([]),
  stripeEnabled: true,
  stripePublicKey: "",
  stripePublishableKey: "",
  paypalEnabled: false,
  paypalClientId: "",
  sumupEnabled: false,
  providers: Object.freeze({}),
});

const EMPTY_GET_ORDER_PAYMENT = Object.freeze({
  ...EMPTY_BRANCH,
  ...EMPTY_CURRENT,
});

const EMPTY_ORDER_PAYMENT = Object.freeze({
  ...EMPTY_BRANCH,
  ...EMPTY_CURRENT,
  reused: false,
  ignored: false,
  idempotencyKey: "",
});

const EMPTY_CANCEL_PENDING = Object.freeze({
  ...EMPTY_BRANCH,
  order: null,
  orderNumber: "",
  payment: null,
  customerCheckout: null,
  paymentWindowExpiresAt: "",
  paymentAction: null,
  idempotencyKey: "",
});

function getBranch(state, key, fallback) {
  return state?.payment?.[key] || fallback;
}

export const selectPaymentState = (state) =>
  state?.payment || EMPTY_PAYMENT_STATE;

export const selectPaymentConfigState = (state) =>
  getBranch(state, "config", EMPTY_PAYMENT_CONFIG);

export const selectPaymentConfig = (state) => selectPaymentConfigState(state);

export const selectIsStripeEnabled = (state) =>
  Boolean(selectPaymentConfigState(state).stripeEnabled);

export const selectStripePublicKey = (state) => {
  const config = selectPaymentConfigState(state);

  return config.stripePublicKey || config.stripePublishableKey || "";
};

export const selectIsPayPalEnabled = (state) =>
  Boolean(selectPaymentConfigState(state).paypalEnabled);

export const selectPayPalClientId = (state) =>
  selectPaymentConfigState(state).paypalClientId || "";

export const selectIsSumUpEnabled = (state) =>
  Boolean(selectPaymentConfigState(state).sumupEnabled);

export const selectGetOrderPaymentState = (state) =>
  getBranch(state, "getOrderPayment", EMPTY_GET_ORDER_PAYMENT);

export const selectOrderPaymentFetch = (state) =>
  selectGetOrderPaymentState(state);

export const selectCreateOrderPaymentState = (state) =>
  getBranch(state, "orderPayment", EMPTY_ORDER_PAYMENT);

export const selectCancelPendingPaymentState = (state) =>
  getBranch(state, "cancelPending", EMPTY_CANCEL_PENDING);

export const selectCurrentPaymentState = (state) =>
  state?.payment?.current || EMPTY_CURRENT;

export const selectCurrentPayment = (state) =>
  selectCurrentPaymentState(state).payment || null;

export const selectCurrentPaymentOrder = (state) =>
  selectCurrentPaymentState(state).order || null;

export const selectCurrentPaymentOrderNumber = (state) =>
  selectCurrentPaymentState(state).orderNumber || "";

export const selectCurrentClientSecret = (state) =>
  selectCurrentPaymentState(state).clientSecret || "";

export const selectCurrentCustomerCheckout = (state) =>
  selectCurrentPaymentState(state).customerCheckout || null;

export const selectCurrentPaymentWindowExpiresAt = (state) =>
  selectCurrentPaymentState(state).paymentWindowExpiresAt || "";

export const selectCurrentApprovalUrl = (state) => {
  const current = selectCurrentPaymentState(state);

  return (
    current.checkoutUrl ||
    current.approvalUrl ||
    current.approveUrl ||
    current.payerActionUrl ||
    ""
  );
};

export const selectCurrentNextAction = (state) =>
  selectCurrentPaymentState(state).nextAction || null;

export const selectAnyPaymentBusy = (state) => {
  const payment = selectPaymentState(state);

  return Boolean(
    payment?.config?.loading ||
    payment?.getOrderPayment?.loading ||
    payment?.orderPayment?.loading ||
    payment?.confirmPayment?.loading ||
    payment?.cancelPending?.loading,
  );
};
